import { useForm } from '@tanstack/react-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'sonner';

import { Button } from '@/components/button/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/dialog/dialog';
import { Input } from '@/components/input/input';
import { Label } from '@/components/label/label';
import Select from '@/components/select/select';

import type { BaseDialogProps } from '@/contexts/dialog-context/v2-base-dialog-props';
import { useV2DialogContext } from '@/contexts/dialog-context/v2-dialog-context';
import { GET_LIST_USER_QUERY_KEY } from '@/features/users/api/get-list-user.api';
import {
  GET_USER_QUERY_KEY,
  getUserQueryOptions,
} from '@/features/users/api/get-user.api';
import {
  updateUserMutationOptions,
  type UpdateUserData,
} from '@/features/users/api/update-user.api';
import { verifyUserMutationOptions } from '@/features/users/api/verify-user.api';
import { toggleUserActiveMutationOptions } from '@/features/users/api/toggle-user-active.api';

const GENDER_OPTIONS = [
  { value: 'male', label: 'Laki-laki' },
  { value: 'female', label: 'Perempuan' },
];

const TYPE_OPTIONS = [
  { value: 'MENTOR', label: 'Mentor' },
  { value: 'USER', label: 'User' },
];

const VERIFIED_OPTIONS = [
  { value: 'true', label: 'Ya' },
  { value: 'false', label: 'Tidak' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const PHONE_VALIDATORS = {
  onChange: ({ value }: { value: string }) => {
    if (!value) return undefined;
    return /^\+62\d{8,12}$/.test(value)
      ? undefined
      : 'Nomor telepon harus diawali +62 dan merupakan nomor Indonesia yang valid';
  },
};

export interface EditUserDialogProps extends BaseDialogProps {
  userId: string;
}

export default function EditUserDialog({
  dialog,
  userId,
}: EditUserDialogProps) {
  const { closeEditUserDialog } = useV2DialogContext();
  const queryClient = useQueryClient();
  const getUserQuery = useQuery(getUserQueryOptions({ user_id: userId }));

  const user = getUserQuery.data?.result;

  const updateUserMutation = useMutation({
    ...updateUserMutationOptions(),
    onSuccess: () => {
      toast.success('Berhasil mengubah data pengguna');
      queryClient.invalidateQueries({ queryKey: [GET_LIST_USER_QUERY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [GET_USER_QUERY_KEY, userId],
      });
      closeEditUserDialog();
    },
    onError: error => {
      const message =
        error instanceof AxiosError
          ? ((error.response?.data as { message?: string })?.message ??
            error.message)
          : 'Gagal mengubah data pengguna';
      toast.error(message);
    },
  });

  const verifyUserMutation = useMutation({
    ...verifyUserMutationOptions(),
    onSuccess: () => {
      toast.success('Berhasil memverifikasi pengguna');
      queryClient.invalidateQueries({ queryKey: [GET_LIST_USER_QUERY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [GET_USER_QUERY_KEY, userId],
      });
    },
    onError: error => {
      const message =
        error instanceof AxiosError
          ? ((error.response?.data as { message?: string })?.message ??
            error.message)
          : 'Gagal memverifikasi pengguna';
      toast.error(message);
    },
  });

  const toggleUserActiveMutation = useMutation({
    ...toggleUserActiveMutationOptions(),
    onSuccess: () => {
      toast.success('Berhasil mengubah status pengguna');
      queryClient.invalidateQueries({ queryKey: [GET_LIST_USER_QUERY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [GET_USER_QUERY_KEY, userId],
      });
    },
    onError: error => {
      const message =
        error instanceof AxiosError
          ? ((error.response?.data as { message?: string })?.message ??
            error.message)
          : 'Gagal mengubah status pengguna';
      toast.error(message);
    },
  });

  const form = useForm({
    defaultValues: {
      full_name: user?.full_name ?? '',
      email: user?.email ?? '',
      gender: (user?.gender as 'male' | 'female' | undefined) ?? undefined,
      phone_number: user?.phone_number ?? '',
      type: (user?.type as 'MENTOR' | 'USER' | undefined) ?? undefined,
      is_verified: user?.is_verified ? 'true' : 'false',
      status: user?.status ?? 'active',
    },
    onSubmit: async ({ value }) => {
      await updateUserMutation.mutateAsync({
        user_id: userId,
        data: {
          full_name: value.full_name,
          email: value.email,
          gender: value.gender,
          phone_number: value.phone_number || null,
          type: value.type,
        } as UpdateUserData,
      });

      // Handle verification separately via PATCH /user/{id}/verify
      if (value.is_verified !== undefined) {
        const isVerified = value.is_verified === 'true';
        if (isVerified !== user?.is_verified) {
          await verifyUserMutation.mutateAsync({
            user_id: userId,
            is_verified: isVerified,
          });
        }
      }

      // Handle status toggle separately via PATCH /user/{id}/toggle-active
      if (value.status !== user?.status) {
        await toggleUserActiveMutation.mutateAsync({
          user_id: userId,
        });
      }

      closeEditUserDialog();
    },
  });

  const isLoading = getUserQuery.isPending;
  const isPending =
    updateUserMutation.isPending ||
    verifyUserMutation.isPending ||
    toggleUserActiveMutation.isPending;

  return (
    <Dialog
      {...dialog}
      onOpenChange={nextOpenState => {
        if (!nextOpenState) {
          closeEditUserDialog();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Pengguna</DialogTitle>
          <DialogDescription>
            Perbarui data pengguna di bawah ini.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex min-h-40 items-center justify-center text-sm text-muted-foreground">
            Memuat data pengguna...
          </div>
        ) : (
          <form
            className="flex flex-col gap-4"
            onSubmit={e => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
          >
            <form.Field name="full_name">
              {field => (
                <div className="grid gap-2">
                  <Label htmlFor={field.name}>Nama Lengkap</Label>
                  <Input
                    id={field.name}
                    placeholder="Masukkan Nama Lengkap"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={e => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="email">
              {field => (
                <div className="grid gap-2">
                  <Label htmlFor={field.name}>Email</Label>
                  <Input
                    id={field.name}
                    type="email"
                    placeholder="Masukkan Email"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={e => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="gender">
              {field => (
                <div className="grid gap-2">
                  <Label>Jenis Kelamin</Label>
                  <Select
                    className="w-full"
                    placeholder="Pilih Jenis Kelamin"
                    options={GENDER_OPTIONS}
                    value={field.state.value}
                    onValueChange={value =>
                      field.handleChange(value as 'male' | 'female')
                    }
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="phone_number" validators={PHONE_VALIDATORS}>
              {field => (
                <div className="grid gap-2">
                  <Label htmlFor={field.name}>Nomor Telepon</Label>
                  <Input
                    id={field.name}
                    placeholder="+628xxxxxxxxxx"
                    value={field.state.value ?? ''}
                    onBlur={field.handleBlur}
                    onChange={e => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.isTouched &&
                    field.state.meta.errors.length > 0 && (
                      <p className="text-sm text-destructive">
                        {String(field.state.meta.errors[0])}
                      </p>
                    )}
                </div>
              )}
            </form.Field>

            <form.Field name="type">
              {field => (
                <div className="grid gap-2">
                  <Label>Tipe</Label>
                  <Select
                    className="w-full"
                    placeholder="Pilih Tipe"
                    options={TYPE_OPTIONS}
                    value={field.state.value}
                    onValueChange={value =>
                      field.handleChange(value as 'MENTOR' | 'USER')
                    }
                  />
                </div>
              )}
            </form.Field>

            {user?.is_verified ? (
              <div className="grid gap-2">
                <Label>Terverifikasi</Label>
                <Select
                  className="w-full"
                  disabled
                  options={VERIFIED_OPTIONS}
                  value="true"
                />
              </div>
            ) : (
              <form.Field name="is_verified">
                {field => (
                  <div className="grid gap-2">
                    <Label>Terverifikasi</Label>
                    <Select
                      className="w-full"
                      placeholder="Pilih Status Verifikasi"
                      options={VERIFIED_OPTIONS}
                      value={field.state.value}
                      onValueChange={value => field.handleChange(value)}
                    />
                  </div>
                )}
              </form.Field>
            )}

            <form.Field name="status">
              {field => (
                <div className="grid gap-2">
                  <Label>Status</Label>
                  <Select
                    className="w-full"
                    placeholder="Pilih Status"
                    options={STATUS_OPTIONS}
                    value={field.state.value}
                    onValueChange={value => field.handleChange(value)}
                  />
                </div>
              )}
            </form.Field>

            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={closeEditUserDialog}
                disabled={isPending}
              >
                Batal
              </Button>
              <form.Subscribe selector={state => [state.canSubmit]}>
                {([canSubmit]) => (
                  <Button type="submit" disabled={!canSubmit || isPending}>
                    {isPending ? 'Menyimpan...' : 'Edit'}
                  </Button>
                )}
              </form.Subscribe>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
